import { useState, useEffect } from "react";
import { useApi } from "@/lib/api";
import { toast } from "sonner";
import { 
  MessageSquareText, 
  Copy, 
  Check,
  Sparkles
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

export default function PitchGenerator() {
  const api = useApi();
  const [platforms, setPlatforms] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [selectedGaps, setSelectedGaps] = useState([]);
  const [tone, setTone] = useState("professional");
  const [pitch, setPitch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPlatforms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlatforms = async () => {
    try {
      const response = await api.get("/api/platforms");
      setPlatforms(response.data);
    } catch (error) {
      console.error("Error fetching platforms:", error);
    } finally {
      setInitialLoading(false);
    }
  };

  const getGapsForPlatform = () => {
    if (!selectedPlatform) return [];
    const platform = platforms.find(p => p.id === selectedPlatform);
    if (!platform) return [];
    return platform.capabilities
      .filter(c => !c.in_house && c.outsource_opportunity !== 'none')
      .map(c => ({
        id: c.category,
        label: c.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        description: c.description
      }));
  };

  const handleGapToggle = (gapId) => {
    setSelectedGaps(prev => 
      prev.includes(gapId) 
        ? prev.filter(g => g !== gapId)
        : [...prev, gapId]
    );
  };

  const generatePitch = async () => {
    if (!selectedPlatform) {
      toast.error("Please select a platform");
      return;
    }
    if (selectedGaps.length === 0) {
      toast.error("Please select at least one gap to address");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/api/pitch/generate", {
        platform_id: selectedPlatform,
        gaps: selectedGaps,
        tone: tone
      });
      setPitch(response.data);
      toast.success("Pitch generated successfully");
    } catch (error) {
      console.error("Error generating pitch:", error);
      toast.error("Failed to generate pitch");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!pitch) return;
    
    const pitchText = `${pitch.pitch_title}

${pitch.value_proposition}

Key Points:
${pitch.key_points.map(p => `• ${p}`).join('\n')}

${pitch.call_to_action}`;
    
    try {
      await navigator.clipboard.writeText(pitchText);
      setCopied(true);
      toast.success("Copied to clipboard");
    } catch (err) {
      // Fallback for environments without clipboard permission
      const textArea = document.createElement("textarea");
      textArea.value = pitchText;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        toast.success("Copied to clipboard");
      } catch (e) {
        toast.error("Could not copy - please select and copy manually");
      }
      document.body.removeChild(textArea);
    }
    setTimeout(() => setCopied(false), 2000);
  };

  if (initialLoading) {
    return (
      <div className="content-wrapper flex items-center justify-center min-h-[50vh]">
        <div className="loading-spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  const gaps = getGapsForPlatform();

  return (
    <div className="content-wrapper" data-testid="pitch-generator-page">
      <div className="page-header compact">
        <h1 className="page-title">Pitch Generator</h1>
        <p className="page-subtitle">
          Generate targeted sales pitches based on compliance gaps
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Configuration Panel - Compact */}
        <div className="space-y-4">
          {/* Platform Selection */}
          <div className="card p-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              1. Select Platform
            </h3>
            <Select value={selectedPlatform} onValueChange={(value) => {
              setSelectedPlatform(value);
              setSelectedGaps([]);
              setPitch(null);
            }}>
              <SelectTrigger className="h-9 bg-gray-50 border-gray-200 text-gray-900" data-testid="platform-select">
                <SelectValue placeholder="Choose target platform" />
              </SelectTrigger>
              <SelectContent>
                {platforms.map((platform) => (
                  <SelectItem key={platform.id} value={platform.id}>
                    {platform.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Gap Selection */}
          {selectedPlatform && (
            <div className="card p-4 animate-fade-in">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                2. Select Gaps to Address
              </h3>
              <div className="space-y-2">
                {gaps.map((gap) => (
                  <div 
                    key={gap.id}
                    className={`p-2 border rounded-lg transition-colors cursor-pointer ${
                      selectedGaps.includes(gap.id) 
                        ? 'border-violet-500 bg-violet-500/10' 
                        : 'border-gray-200 hover:border-violet-500/50'
                    }`}
                    onClick={() => handleGapToggle(gap.id)}
                  >
                    <div className="flex items-start gap-2">
                      <Checkbox 
                        checked={selectedGaps.includes(gap.id)}
                        onCheckedChange={() => handleGapToggle(gap.id)}
                        className="mt-0.5"
                        data-testid={`gap-${gap.id}`}
                      />
                      <div>
                        <Label className="font-medium text-gray-900 text-sm cursor-pointer">
                          {gap.label}
                        </Label>
                        <p className="text-[10px] text-gray-500 mt-0.5">{gap.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {selectedGaps.length > 0 && (
                <div className="mt-2 text-xs text-violet-500">
                  {selectedGaps.length} gap{selectedGaps.length > 1 ? 's' : ''} selected
                </div>
              )}
            </div>
          )}

          {/* Tone Selection */}
          {selectedGaps.length > 0 && (
            <div className="card p-4 animate-fade-in">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                3. Select Tone
              </h3>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="h-9" data-testid="tone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="consultative">Consultative</SelectItem>
                  <SelectItem value="direct">Direct</SelectItem>
                </SelectContent>
              </Select>

              <button
                onClick={generatePitch}
                disabled={loading}
                className="btn-primary w-full mt-3 justify-center"
                data-testid="generate-pitch-btn"
              >
                {loading ? (
                  <>
                    <div className="loading-spinner w-4 h-4"></div>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    <span>Generate Pitch</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Pitch Output - Compact */}
        <div>
          {pitch ? (
            <div className="pitch-output animate-fade-in" data-testid="pitch-output">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                  Generated Pitch
                </span>
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-1 text-xs text-violet-500 hover:text-violet-400 transition-colors"
                  data-testid="copy-pitch-btn"
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  <span>{copied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>

              <h2 className="pitch-title">{pitch.pitch_title}</h2>
              
              <p className="pitch-value-prop">{pitch.value_proposition}</p>

              <div className="pitch-points">
                <h4>Key Points</h4>
                <ul>
                  {pitch.key_points.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
              </div>

              <div className="pitch-cta">
                <strong>Call to Action:</strong> {pitch.call_to_action}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap gap-1.5">
                  <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded">
                    Platform: {pitch.platform}
                  </span>
                  <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded">
                    Tone: {tone}
                  </span>
                  <span className="px-1.5 py-0.5 bg-violet-500/15 text-violet-500 text-[10px] rounded">
                    {selectedGaps.length} gap{selectedGaps.length > 1 ? 's' : ''} addressed
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="card p-8 text-center">
              <MessageSquareText size={36} className="mx-auto mb-3 text-gray-300" />
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Your Pitch Will Appear Here</h3>
              <p className="text-xs text-gray-500">
                Select a platform, choose gaps to address, and generate a pitch
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
